import { motion } from "framer-motion";
import { ExternalLink, TrendingUp, Users, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  containerVariants,
  titleVariants,
  textVariants,
} from "../utils/animations";
import LogoStrip from "../components/LogoStrip";

// Success Story Card Component
const StoryCard = ({ story, index }) => {
  const navigate = useNavigate();

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        delay: index * 0.2,
      },
    },
    hover: {
      scale: 1.02,
      y: -5,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  const iconVariants = {
    hidden: { opacity: 0, scale: 0, rotate: -90 },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delay: 0.2 + index * 0.2,
      },
    },
  };

  const handleCardClick = () => {
    navigate(story.link);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleCardClick();
    }
  };

  return (
    <motion.div
      className="group bg-white p-8 rounded-xl shadow-lg hover:shadow-xl cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#C5A66A] focus:ring-offset-2"
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      whileHover="hover"
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`${story.title} - Mehr erfahren`}
    >
      <div className="flex flex-col space-y-4">
        <div className="flex items-center space-x-4">
          <motion.div
            className="w-16 h-16 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0"
            style={{ backgroundColor: story.bgColor }}
            variants={iconVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            <story.icon size={24} />
          </motion.div>
          <motion.h3
            className="text-xl font-bold text-[#0F1B2D] group-hover:text-[#C5A66A] transition-colors flex-1"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: 0.3 + index * 0.2 }}
          >
            {story.title}
          </motion.h3>
        </div>

        <motion.p
          className="text-gray-600 leading-relaxed w-full"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5, delay: 0.4 + index * 0.2 }}
        >
          {story.description}
        </motion.p>

        <motion.div
          className="flex items-center justify-between w-full"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5, delay: 0.5 + index * 0.2 }}
        >
          <div className="flex items-center space-x-2">
            <span className="text-sm font-semibold text-[#C5A66A]">
              {story.metric}
            </span>
          </div>
          <ExternalLink
            size={16}
            className="text-gray-400 group-hover:text-[#C5A66A] transition-colors"
          />
        </motion.div>
      </div>
    </motion.div>
  );
};

// Success Stories Section
const StoriesSection = () => {
  const { t } = useTranslation();

  const successStories = [
    {
      title: t("stories.stories.ai.title"),
      description: t("stories.stories.ai.description"),
      metric: t("stories.stories.ai.metric"),
      icon: Zap,
      bgColor: "#C5A66A",
      link: "/ai",
    },
    {
      title: t("stories.stories.digital.title"),
      description: t("stories.stories.digital.description"),
      metric: t("stories.stories.digital.metric"),
      icon: TrendingUp,
      bgColor: "#0F1B2D",
      link: "/digital",
    },
    {
      title: t("stories.stories.expansion.title"),
      description: t("stories.stories.expansion.description"),
      metric: t("stories.stories.expansion.metric"),
      icon: Users,
      bgColor: "#C5A66A",
      link: "/strategie",
    },
  ];

  return (
    <section className="pt-32 pb-20 bg-[#0F1B2D]">
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.h2
            className="font-playfair text-4xl lg:text-5xl font-bold text-[#C5A66A] mb-6"
            variants={titleVariants}
          >
            {t("stories.title")}
          </motion.h2>
          <div className="bg-white rounded-2xl shadow-luxury max-w-5xl mx-auto mb-10 px-8 py-7">
            <LogoStrip />
          </div>
          <motion.p
            className="text-xl text-gray-300 max-w-3xl mx-auto"
            variants={textVariants}
          >
            {t("stories.subtitle")}
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {successStories.map((story, index) => (
            <StoryCard key={index} story={story} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default StoriesSection;
